import { useEffect, useState } from "react";
import Loader from "./components/Loader/Loader";
import Navbar from "./components/Navbar/Navbar";

import Home from "./sections/Home/Home.jsx";
import About from "./sections/About/About.jsx";
import Portfolio from "./sections/Portfolio/Portfolio.jsx";
import Contact from "./sections/Contact/Contact.jsx";

function App() {
  const [loading, setLoading] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [snakeTrail, setSnakeTrail] = useState([]);
  const [snakeVisible, setSnakeVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2700);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition =
        document.documentElement.scrollTop || document.body.scrollTop;
      const totalHeight =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight;
      const progress =
        totalHeight <= 0
          ? 0
          : Math.min((scrollPosition / totalHeight) * 100, 100);
      setScrollProgress(progress);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    let idleTimeout;
    const MAX_TRAIL = 18;

    const handleMove = (event) => {
      const { clientX, clientY } = event;
      setSnakeTrail((prev) => {
        const next = [
          {
            x: clientX,
            y: clientY,
            timestamp: Date.now(),
          },
          ...prev,
        ];
        if (next.length > MAX_TRAIL) {
          next.pop();
        }
        return next;
      });
      setSnakeVisible(true);
      clearTimeout(idleTimeout);
      idleTimeout = setTimeout(() => {
        setSnakeVisible(false);
        setSnakeTrail([]);
      }, 800);
    };

    const handleLeave = () => {
      setSnakeVisible(false);
      setSnakeTrail([]);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseleave", handleLeave);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseleave", handleLeave);
      clearTimeout(idleTimeout);
    };
  }, []);

  useEffect(() => {
    if (loading) return undefined;

    const revealed = document.querySelectorAll(
      ".reveal-left, .reveal-right, .reveal-up"
    );

    if (!revealed.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.18,
        rootMargin: "0px 0px -8% 0px",
      }
    );

    revealed.forEach((element) => observer.observe(element));

    return () => {
      observer.disconnect();
    };
  }, [loading]);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <div className="scroll-progress">
        <span style={{ width: `${scrollProgress}%` }} />
      </div>
      <div className={`mouse-snake ${snakeVisible ? "visible" : ""}`}>
        {snakeTrail.map((point, index) => {
          const nextPoint = snakeTrail[index + 1] ?? point;
          const dx = nextPoint.x - point.x;
          const dy = nextPoint.y - point.y;
          const distance = Math.max(Math.hypot(dx, dy), 6);
          const angle = Math.atan2(dy, dx);
          const opacity = (snakeTrail.length - index) / snakeTrail.length;
          return (
            <span
              key={`${point.timestamp}-${index}`}
              style={{
                left: point.x,
                top: point.y,
                height: `${distance}px`,
                transform: `translate(-50%, -50%) rotate(${angle}rad)`,
                opacity,
              }}
            />
          );
        })}
      </div>
      <Navbar />
      <Home />
      <About />
      <Portfolio />
      <Contact />
    </>
  );
}

export default App;
